import csvToJson from 'convert-csv-to-json';

import { buildInitialExportName } from '@/features/DataHub/modules/Export/services/ExportNameService';
import type { CsvWidgetExportSource } from '@/features/DataHub/modules/Export/types/context.types';
import downloadTextFileFromDatalake from '@/utils/downloadTextFileFromDatalake';
import { buildExportFileDateSuffix } from '@/utils/exportFileDateSuffix';
import { parseFromToISO } from '@/utils/parseFromToISO';
import { parseAndFormatUserDateString } from '@/utils/parseUserDateString';
import type { DateFormattingConfig } from '@/utils/parseUserDateString';
import { queryApiByIdString } from '@/utils/queryApi';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';

// Matches ISO date (YYYY-MM-DD or YYYY-MM-DDThh…), DDMonYY (e.g. 05Aug25),
// DD-Mon-YY (e.g. 05-Mar-26), or Mon-YY (e.g. Dec-25)
const DATE_PATTERNS = [
  /^\d{4}-\d{2}-\d{2}/,
  /^\d{2}[A-Za-z]{3}\d{2}$/,
  /^\d{2}-[A-Za-z]{3}-\d{2}$/,
  /^[A-Za-z]{3}-\d{2}$/
];

function isDateValue(value: unknown): boolean {
  if (typeof value !== 'string' || value.length === 0 || value.length > 50) return false;
  return DATE_PATTERNS.some(re => re.test(value));
}

function tryParseDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseCsvRows(rawCsv: string): Record<string, unknown>[] {
  if (!rawCsv.trim()) return [];
  return csvToJson
    .formatValueByType(true)
    .supportQuotedField(true)
    .fieldDelimiter(',')
    .csvStringToJson(rawCsv) as Record<string, unknown>[];
}

function detectDateColumn(rows: Record<string, unknown>[], header: string[]): string | null {
  if (rows.length === 0 || header.length === 0) return null;
  const sample = rows.slice(0, Math.min(5, rows.length));
  for (const key of header) {
    if (sample.every(row => isDateValue(row[key]))) return key;
  }
  return null;
}

function serializeToCsv(rows: Record<string, unknown>[], header: string[]): string {
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replaceAll('"', '""')}"`
      : s;
  };
  const lines = [
    header.map(escape).join(','),
    ...rows.map(row => header.map(h => escape(row[h])).join(','))
  ];
  return lines.join('\n');
}

function countCsvRowsFromText(rawCsv: string): number {
  const lines = rawCsv
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  return Math.max(lines.length - 1, 0);
}

function resolveDateColumn(
  rows: Record<string, unknown>[],
  header: string[],
  dateColumnKey: string | null
): string | null {
  if (dateColumnKey && header.includes(dateColumnKey)) {
    return dateColumnKey;
  }

  return detectDateColumn(rows, header);
}

function detectFirstDateColumn(rows: Record<string, unknown>[], header: string[]): string | null {
  const firstColumn = header[0];
  if (!firstColumn || rows.length === 0) return null;
  const sample = rows.slice(0, Math.min(5, rows.length));
  return sample.every(row => isDateValue(row[firstColumn])) ? firstColumn : null;
}

function resolveLocalFilterDateColumn(
  source: CsvWidgetExportSource,
  rows: Record<string, unknown>[],
  header: string[],
  dateColumnKey: string | null
): string | null {
  if (source.sourceType === 'query') {
    return detectFirstDateColumn(rows, header);
  }

  return resolveDateColumn(rows, header, dateColumnKey);
}

function formatDateColumn(
  rows: Record<string, unknown>[],
  dateColumnKey: string | null,
  dateFormattingConfig?: DateFormattingConfig
): Record<string, unknown>[] {
  if (!dateFormattingConfig || !dateColumnKey || rows.length === 0) return rows;

  return rows.map(row => {
    const value = row[dateColumnKey];
    if (typeof value !== 'string') return row;

    const { formatted, matched } = parseAndFormatUserDateString(value, dateFormattingConfig);
    if (!matched || formatted === value) return row;

    return { ...row, [dateColumnKey]: formatted };
  });
}

function resolveQueryFrom(
  selectedRange: string | null | undefined,
  from: string | undefined
): string | undefined {
  if (!selectedRange) return from ?? undefined;
  if (!from) return selectedRange;

  const selectedRangeDate = tryParseDate(parseFromToISO(selectedRange));
  const fromDate = tryParseDate(from);

  if (!selectedRangeDate || !fromDate) return from;
  return selectedRangeDate > fromDate ? selectedRangeDate.toISOString() : fromDate.toISOString();
}

function filterRowsByDate(
  allRows: Record<string, unknown>[],
  from: string | undefined,
  to: string | undefined,
  dateColumnKey: string | null
) {
  if (allRows.length === 0) return allRows;
  const shouldFilter = (from !== undefined || to !== undefined) && allRows.length > 0;
  if (!shouldFilter) return allRows;

  if (!dateColumnKey) {
    return allRows;
  }

  const fromDate = from ? tryParseDate(from) : null;
  const toDate = to ? tryParseDate(to) : null;

  return allRows.filter(row => {
    const date = tryParseDate(row[dateColumnKey]);
    if (!date) return true;
    if (fromDate && date < fromDate) return false;
    if (toDate && date > toDate) return false;
    return true;
  });
}

async function fetchCsvText(
  source: CsvWidgetExportSource,
  from: string | undefined,
  to: string | undefined
): Promise<string> {
  if (source.sourceType === 'query') {
    const queryFrom = resolveQueryFrom(source.selectedRange, from);
    return queryApiByIdString(source.queryFileId, queryFrom, to);
  }
  return downloadTextFileFromDatalake(source.fileName, source.groupName);
}

function buildCsvWidgetExportFilename(
  exportName: string,
  dateFormattingConfig?: DateFormattingConfig
): string {
  const baseName = buildInitialExportName(exportName, 'TradeData');
  const nowIso = new Date().toISOString();
  const formattedNow = dateFormattingConfig
    ? parseAndFormatUserDateString(nowIso, dateFormattingConfig).formatted
    : '';
  const dateSuffix = buildExportFileDateSuffix(formattedNow, nowIso);

  if (baseName.toLowerCase().endsWith(dateSuffix.toLowerCase())) {
    return `${baseName}.csv`;
  }

  return `${baseName}-${dateSuffix}.csv`;
}

export type PreparedCsvWidgetExport = {
  blob: Blob;
  fileName: string;
  rowCount: number;
};

export type CsvWidgetExportDownloadParams = {
  source: CsvWidgetExportSource;
  from: string | undefined;
  to: string | undefined;
  dateColumnKey: string | null;
  exportName: string;
  dateFormattingConfig?: DateFormattingConfig;
};

export async function prepareCsvWidgetExport({
  source,
  from,
  to,
  dateColumnKey,
  exportName,
  dateFormattingConfig
}: CsvWidgetExportDownloadParams): Promise<PreparedCsvWidgetExport> {
  const fileName = buildCsvWidgetExportFilename(exportName, dateFormattingConfig);
  const rawCsv = await fetchCsvText(source, from, to);

  if (!rawCsv.trim()) {
    return {
      blob: new Blob([rawCsv], { type: 'text/csv' }),
      fileName,
      rowCount: 0
    };
  }

  const allRows = parseCsvRows(rawCsv);
  if (allRows.length === 0) {
    return {
      blob: new Blob([rawCsv], { type: 'text/csv' }),
      fileName,
      rowCount: 0
    };
  }

  const header = Object.keys(allRows[0]);
  const resolvedDateColumn = resolveDateColumn(allRows, header, dateColumnKey);
  const filterDateColumn = resolveLocalFilterDateColumn(source, allRows, header, dateColumnKey);
  const shouldFilterByDate = (from !== undefined || to !== undefined) && Boolean(filterDateColumn);
  const shouldFormatDates = Boolean(dateFormattingConfig && resolvedDateColumn);

  if (!shouldFilterByDate && !shouldFormatDates) {
    return {
      blob: new Blob([rawCsv], { type: 'text/csv' }),
      fileName,
      rowCount: source.sourceType === 'query' ? countCsvRowsFromText(rawCsv) : allRows.length
    };
  }

  const filteredRows = shouldFilterByDate
    ? filterRowsByDate(allRows, from, to, filterDateColumn)
    : allRows;
  const rowsToExport = formatDateColumn(filteredRows, resolvedDateColumn, dateFormattingConfig);

  const csvText = serializeToCsv(rowsToExport, header);
  return {
    blob: new Blob([csvText], { type: 'text/csv' }),
    fileName,
    rowCount: rowsToExport.length
  };
}

export async function downloadCsvWidgetExport(
  params: CsvWidgetExportDownloadParams
): Promise<number> {
  const preparedExport = await prepareCsvWidgetExport(params);
  triggerBlobDownload(preparedExport.blob, preparedExport.fileName);
  return preparedExport.rowCount;
}

export async function countCsvWidgetExportRows(
  source: CsvWidgetExportSource,
  from: string | undefined,
  to: string | undefined,
  dateColumnKey: string | null
): Promise<number> {
  const rawCsv = await fetchCsvText(source, from, to);
  const allRows = parseCsvRows(rawCsv);
  if (allRows.length === 0) return 0;
  const header = Object.keys(allRows[0]);
  const filterDateColumn = resolveLocalFilterDateColumn(source, allRows, header, dateColumnKey);

  if (!filterDateColumn || (from === undefined && to === undefined)) {
    return allRows.length;
  }

  const filteredRows = filterRowsByDate(allRows, from, to, filterDateColumn);
  return filteredRows.length;
}
